/**
 * Main AutoRender client factory
 * Creates an AR instance with URL building and responsive image capabilities
 */

import { buildImageUrl } from './url-builder';
import { buildTransformString } from './transform';
import {
  generateResponsiveAttributes,
  DEFAULT_DEVICE_BREAKPOINTS,
  DEFAULT_IMAGE_BREAKPOINTS
} from './responsive';
import { getDPR } from './device';
import type {
  CreateARConfig,
  ARInstance,
  TransformOptions,
  ResponsiveOptions,
  ResponsiveAttributes
} from './types';

const DEFAULT_BASE_URL = 'https://assets.autorender.io';

export function createAR(config: CreateARConfig): ARInstance {
  const {
    baseUrl = DEFAULT_BASE_URL,
    workspace,
    defaults = {},
    deviceBreakpoints = DEFAULT_DEVICE_BREAKPOINTS,
    imageBreakpoints = DEFAULT_IMAGE_BREAKPOINTS,
    enableDPR = true
  } = config;
  
  if (!workspace) {
    throw new Error('workspace is required');
  }
  
  const instance: ARInstance = {
    /**
     * Generate image URL with transformations
     */
    url(src: string, transform?: TransformOptions): string {
      // `enableDPR` is deliberately not forwarded: it governs the `2x` candidate
      // in `responsiveImageAttributes`, not this single URL, which must be the
      // same string on the server and in the browser.
      return buildImageUrl(
        baseUrl,
        workspace,
        src,
        transform,
        defaults
      );
    },

    /**
     * Generate transformation string only (without URL)
     */
    transformString(transform: TransformOptions): string {
      return buildTransformString(
        transform,
        defaults
      );
    },
    
    /**
     * Generate responsive image attributes
     */
    responsiveImageAttributes(options: ResponsiveOptions): ResponsiveAttributes {
      // Allow per-call breakpoint override
      const effectiveDeviceBreakpoints = options.breakpoints || deviceBreakpoints;
      
      return generateResponsiveAttributes(
        baseUrl,
        workspace,
        options,
        defaults,
        effectiveDeviceBreakpoints,
        imageBreakpoints,
        enableDPR
      );
    },
    
    /**
     * Get current device pixel ratio
     */
    getDPR(): number {
      return getDPR();
    }
  };
  
  return instance;
}

